import { tracked } from '@glimmer/tracking';
import Store from '@ember-data/store';
import JournalDashboardModel from 'donor-elf-web/models/journal-dashboard';
import JournalReportIntervarsity from './journal-report-intervarsity';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import JournalInfo from './journal-info';
import AllFundsJournalSlimModel from 'donor-elf-web/models/all-funds/journal-slim';
import JournalReportModel from 'donor-elf-web/models/all-funds/journal-report';
import JournalContactInfo from './journal-contact-info';
import JournalContactDetailModel from 'donor-elf-web/models/all-funds/journal-contact-detail';
import FundsService from 'donor-elf-web/services/funds-service';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import JournalReportDefault from './journal-report-default';
import JournalStageModel from 'donor-elf-web/models/journal-stage';
import JournalGoalModel from 'donor-elf-web/models/journal-goal';
import {
  GetIntervalCumulativeAmounts,
  type IntervalCumulativeAmount,
} from '../journal-utils';

export default class JournalDigestInfo {
  constructor(
    journalSlim: AllFundsJournalSlimModel,
    store: Store,
    fundsService: FundsService,
    formatDateService: FormatDateService,
  ) {
    this.journalSlim = journalSlim;
    this.store = store;
    this.formatDateService = formatDateService;
    this.viewJournalUrl = fundsService.viewFundUrl(journalSlim.fundId, {
      urlPath: `journals/${journalSlim.id}`,
    });
  }

  journalSlim: AllFundsJournalSlimModel;
  journalInfo?: JournalInfo;
  store: Store;
  formatDateService: FormatDateService;

  @tracked loading = true;
  @tracked journalDashboard?: JournalDashboardModel;
  @tracked journalContactInfos?: JournalContactInfo[];
  @tracked journalReport?: JournalReportModel;
  @tracked reportDefault?: JournalReportDefault;
  @tracked reportIntervarsity?: JournalReportIntervarsity;
  @tracked showProjectedGoal = false;
  engageStage?: JournalStageModel;
  askStage?: JournalStageModel;
  mainGoalAndDecisions?: {
    goal: JournalGoalModel;
    intervalCumulativeAmounts: IntervalCumulativeAmount[];
  };

  get selectedIntervalItems() {
    if (this.reportDefault) {
      return this.reportDefault.intervalItems.slice(
        Math.max(this.reportDefault.intervalItems.length - 6, 0),
      );
    }
    return [];
  }

  get firstGoalDecisionPercent(): number | undefined {
    if (this.loading) {
      return undefined;
    }
    let firstGoal = this.journalInfo?.goals[0];
    if (firstGoal == null) {
      return undefined;
    }
    let lastIntervalItem =
      this.reportDefault?.intervalItems[
        this.reportDefault.intervalItems.length - 1
      ];
    if (lastIntervalItem == null) {
      return undefined;
    }
    return lastIntervalItem.decisions[firstGoal.id]
      .yesCumulativePercent as number;
  }

  viewJournalUrl: string;

  async load() {
    let digest = (
      await this.store.query('all-funds/journal-digest', {
        filter: { journal_ids: [this.journalSlim.id] },
      })
    ).slice()[0];
    this.journalDashboard = digest.journalDashboard;
    let journalInfo = new JournalInfo(digest.journal);
    this.journalInfo = journalInfo;
    this.engageStage =
      journalInfo.sortedStages.find((stage) => stage.name.includes('Engage')) ||
      journalInfo.sortedStages[0];
    this.engageStage.weeklyGoal = 30;
    this.askStage = journalInfo.askStage;
    this.askStage.weeklyGoal = 4;
    let journalContactInfos: JournalContactInfo[] = [];
    await queryWithPages<JournalContactDetailModel>({
      store: this.store,
      modelName: 'all-funds/journal-contact-detail',
      queryOptions: {
        filter: {
          journal_id: this.journalSlim.id,
        },
      },
      pageSize: 200,
      pageCallback: (args) => {
        journalContactInfos.push(
          ...args.pageRecords.map(
            (journalContactDetailModel) =>
              new JournalContactInfo({
                journalContactDetailModel,
                journalInfo,
              }),
          ),
        );
      },
    });
    this.journalContactInfos = journalContactInfos;
    this.journalReport = (
      await this.store.query('all-funds/journal-report', {
        filter: { journal_id: this.journalSlim.id },
      })
    ).slice()[0];
    if (this.journalInfo.journal.reportName === 'intervarsity') {
      this.reportIntervarsity = new JournalReportIntervarsity({
        journalInfo,
        journalReport: this.journalReport,
        journalContactInfos,
        formatDateService: this.formatDateService,
      });
      let defaultReport = (
        await this.store.query('all-funds/journal-report', {
          filter: { journal_id: this.journalSlim.id, report_name: 'default' },
        })
      ).slice()[0];
      this.reportDefault = new JournalReportDefault({
        journalInfo,
        journalReportModel: defaultReport,
        formatDateService: this.formatDateService,
      });
    }
    let mainGoal = journalInfo.goals[0];
    this.mainGoalAndDecisions = {
      goal: mainGoal,
      intervalCumulativeAmounts: GetIntervalCumulativeAmounts(
        this.reportDefault!.intervalDates,
        this.reportDefault!.yesDecisions[mainGoal.id as any],
      ),
    };
    this.loading = false;
  }
}
